<template>
<div class="month-kpi-summary">
    <h2 class="margin-bottom-regular">Monats&shy;aus&shy;wer&shy;tung {{ month.formatKapitelMonth(selectedMonth, 'MMMM yyyy')}}</h2>
    <IonLabel class="hint">
        Dein effektiver Stundenlohn unter Berücksichtigung deiner individuellen Überschussbeteiligung &ndash;
        <DetailsPopover label="Mehr erfahren" text-only inline side="bottom">
            <!--<p class="globalJustifyHyphenate">Der Stundenlohn jedes IBFG Mitgliedes wird durch eine einheitlichen IBFG-Lohntabelle festgelegt. Mit dem Jahresabschluss werden die Überschüsse individualisiert ausgeschüttet (siehe <i>Vereinsüberschuss</i>).</p>
            <p class="globalJustifyHyphenate">Mit deinen bislang angesammelten Überschussanteilen für {{month.formatKapitelYear(month.getKapitelYearStringFromKapitelMonthString(props.selectedMonth))}} wäre das ein effektiver Aufschlag auf den Stundenlohn von {{renderSet.excessHourly ? money.formatMoney(renderSet.excessHourly, {signDisplay:'exceptZero'}) : '?'}}.</p>-->



            <p class="globalJustifyHyphenate"> Das Arbeitsentgelt setzt sich im Wesentlichen aus drei Komponenten zusammen. Diese sind der tarifvertragliche Stundenlohn (TVS), die übertarifliche Zulage (ÜTZ) und der individuelle Überschussanteil (IÜA). Hinzu kommen weitere Entgeltbestandteile für Arbeit zu besonderen Zeiten, Mehrarbeit und tarifvertragliche Sonderzahlungen.</p>
            <p class="globalJustifyHyphenate"> TVS und ÜTZ sind einheitlich in der Höhe über alle Arbeitnehmer einer Qualifikation. IÜB hingegen ist ein individueller Entgeltbestandteil, der sich an der individuellen Produktivität orientiert. Der Effektive Stundenlohn ist die Summe aus TVS, ÜTZ und IÜB pro Stunde .
            </p>
            <p class="globalJustifyHyphenate">Jeder weitere Mitarbeiter reduziert die relativen Kosten für alle. Dadurch wächst der Jahresüberschuss und die IÜA steigt mit der Menge der im ines-Modell Angestellten.</p>
        </DetailsPopover>
    </IonLabel>

    <Skeleton v-if="initialDataLoader.pending.value" :items="['block--height-250px']"/>
    <template v-else>
        <div
            class="summary floating-big-darker"
            :class="{'perspective': renderSetIsPerspective}"
            >
            <h1 class="no-margin-top">
                {{ money.formatMoney(currentRenderSet.totalHourly) }}
                <span v-if="renderSetIsPerspective" class="perspective-plus">+{{ money.formatMoney(totalHourlyDiff) }}</span>
            </h1>
            <span>{{renderSetIsPerspective ? 'möglicher' : 'effektiver'}} Stundenlohn</span>

            <p class="margin-top-regular">
                {{renderSetIsPerspective ? 'bis zu ' : ''}}<span class="value">{{ money.formatLargeMoney(currentRenderSet.excessShare) }}</span> Jahresüberschuss<br>
                {{renderSetIsPerspective ? '& ' : ''}}<span class="value">{{ money.formatLargeMoney(currentRenderSet.myExcessShare) }}</span> individueller Anteil
            </p>

            <Range
                style="width: calc(100% - 9rem); margin-top: 1.5rem;"
                :min="0"
                :min-value="renderSetIndex"
                :max="virtualRenderSets.length - 1"
                v-model="currentRenderSetIndex"
                :color="renderSetIsPerspective ? 'green' : undefined"
                snaps
                :knob-label-formatter="() => `&#8249;&nbsp;&nbsp;${currentRenderSet?.numberOfActiveEmployees} Mitarbeiter&nbsp;&nbsp;&#8250;`"
            />
            
        </div>
    </template>
</div>
</template>

<script setup lang="ts">
import {computed, ComputedRef, onMounted, PropType, Ref, ref, watch} from 'vue';
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {IbfgExcessShareSzenario, IbfgYearlyFinancialResultSummary, PayrollStatus} from "@/graphql/generated/graphql";

import {kapitelDateString, kapitelMonthString, kapitelYearString} from '@/graphql/kapitelTypes';
import {
    fetchPayrollStatusForMonth,
    setPayrollStatusSeen
} from "@/helper/monthKPI/monthKpiPayroll";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import {IonCard,IonLabel} from "@ionic/vue";
import {toMap} from "@/helper/array";
import {
    fetchIbfgFinancialResult,
    fetchIbfgFinancialResultSzenarios,
    fetchIbfgFinancialResultToDate,
    getLastIncludedMonthlyResult
} from "@/helper/monthKPI/monthKpiIBFG";
import month from "@/helper/datetime/month";
import ApexChartSummaryLine from "@/views/components/ApexChartSummaryLine.vue";
import * as money from "@/helper/money";
import DetailsPopover from "@/views/components/DetailsPopover.vue";
import date from "@/helper/datetime/date";
import Blocks from "@/views/components/Blocks.vue";
import Block from "@/views/components/Block.vue";
import {pluralize} from "@/helper/amountFormatter";
import InlineSVG from "@/components/InlineSVG.vue";
import RecruitingBlock from "@/views/components/MonthKPI/subcomponents/RecruitingBlock.vue";
import Transitionator from '../../transitions/Transitionator.vue';
import Fireworks from "@/components/Fireworks.vue";
import {useGlobalEmitter} from "@/helper/emitter";
import WritingText from '@/components/WritingText.vue';
import CountingNumber from '@/components/CountingNumber.vue';
import { Money } from '@/graphql/generated/graphql';
import {getMostRecentPublishedMonth, isMostRecentPublishedMonth} from "@/helper/monthKPI/monthKPI";
import {json} from "agent-base";
import {multiply} from "@/helper/money";
import Range from '@/components/Range.vue';
import { ease } from '@/libs/animation/easer';

// props
const props = defineProps({
    selectedMonth: {
        type: String as PropType<kapitelMonthString>,
        required: true,
    }
});

// loader
const initialDataLoader = new DataLoader();

/**
 * raw data
 */
const ibfgYearSummary : Ref<IbfgYearlyFinancialResultSummary|undefined> = ref(undefined)
const ibfgMonthSummary = computed(() => ibfgYearSummary.value ? getLastIncludedMonthlyResult(ibfgYearSummary.value) : undefined)
initialDataLoader.add(
    async () => ibfgYearSummary.value = await fetchIbfgFinancialResultToDate(props.selectedMonth)
)
const payrollStatusForMonth : Ref<PayrollStatus | undefined> = ref(undefined);
initialDataLoader.add(async () => payrollStatusForMonth.value = await fetchPayrollStatusForMonth(props.selectedMonth))



const renderSet = computed(() => {
    const baseHourly = payrollStatusForMonth.value?.basisHourlyWage ?? null
    const excessHourly = ibfgYearSummary.value?.myHourlyPlusByExcessShare ?? null

    return {
        baseHourly,
        excessHourly,
        totalHourly: baseHourly && excessHourly ? money.add(baseHourly, excessHourly) : null,

        excessShare: ibfgYearSummary.value && ibfgYearSummary.value.lastMonthIncluded === props.selectedMonth ? ibfgYearSummary.value.cumSumResults : null,
        myExcessShare: ibfgYearSummary.value && ibfgYearSummary.value.lastMonthIncluded === props.selectedMonth ? ibfgYearSummary.value.myExcessShare : null,

        numberOfActiveEmployees: ibfgMonthSummary.value?.cntActiveEmployees ?? null,
    }
})

const financialResultScenarios : Ref<IbfgExcessShareSzenario[]|undefined> = ref([])
initialDataLoader.add(async () => financialResultScenarios.value = await fetchIbfgFinancialResultSzenarios(props.selectedMonth))

const virtualRenderSets = computed(() => (financialResultScenarios.value ?? []).map((scenario) => {
    const baseHourly = renderSet.value.baseHourly
    const excessHourly = scenario.myHourlyPlusByExcessShare

    return {
        baseHourly,
        excessHourly,
        totalHourly: baseHourly && excessHourly ? money.add(baseHourly, excessHourly) : null,

        excessShare: scenario.cumSumResults,
        myExcessShare: scenario.myExcessShare,

        numberOfActiveEmployees: scenario.virtualEmployeeCount,
    }
}))

const currentRenderSetIndex = ref(0)
const currentRenderSet = computed(() => virtualRenderSets.value[currentRenderSetIndex.value])
const renderSetIndex = computed(() => virtualRenderSets.value.findIndex(rs => rs.numberOfActiveEmployees === renderSet.value.numberOfActiveEmployees))
const renderSetIsPerspective = computed(() => currentRenderSetIndex.value > renderSetIndex.value)
const totalHourlyDiff = computed(() => {
    const currentAmount = currentRenderSet.value.totalHourly?.amount || 0
    const renderSetAmount = renderSet.value.totalHourly?.amount || 0

    return {
        amount: Math.max(0, currentAmount - renderSetAmount),
        currency: renderSet.value.totalHourly?.currency
    }
})
watch(initialDataLoader.done, () => {
    const currentIndex = renderSetIndex.value
    ease({
        duration: 1000,
        delay: 1000,
        onTick: (progress: number) => {
            currentRenderSetIndex.value = Math.round(progress * currentIndex)
        }
    })
    emit('animationDone')
}, {once: true})



/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()


/**
 * Animation
 */

const emit = defineEmits(['animationDone'])

</script>

<style lang="scss" scoped>
.month-kpi-summary {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex: 1 0 0;

    transition-property: flex;
    transition-duration: .5s;
    transition-timing-function: ease-out;

    > h2 {
        white-space: nowrap;
        transform: translateX();
        width: 0;
        text-align: center;

        transition-property: width;
        transition-duration: .5s;
        transition-timing-function: ease-out;
    }

    @keyframes scale-bounce-in {
        0% {
            opacity: 0;
            transform: scale(3);
        }
        1% {
            opacity: 0;
            transform: scale(3);
        }
        80% {
            opacity: 1;
            transform: scale(0.9);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }

    @keyframes grow-bounce {
        0% {
            transform: scale(1);
        }
        88% {
            transform: scale(1.4);
        }
        100% {
            transform: scale(1);
        }
    }

    @keyframes shake {
        0% {
            transform: rotate(0deg);
        }
        25% {
            transform: rotate(2deg);
        }
        50% {
            transform: rotate(0deg);
        }
        75% {
            transform: rotate(-2deg);
        }
        100% {
            transform: rotate(0deg);
        }
    }
}

.summary {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 1em;

    background-color: var(--ion-color-superblack);
    border-radius: 2rem;
    padding: 1.5rem;

    h1 {
        font-weight: var(--custom-font-weight-extrabold);
        font-size: var(--custom-font-size-extralarge);
        margin-bottom: 0;
        position: relative;

        .perspective-plus {
            position: absolute;
            top: 1.3rem;
            left: calc(100% + 0.8em);
            font-size: var(--custom-font-size-regular);
            background-color: var(--ion-color-white);
            color: var(--ion-color-superblack);
            padding: 0.4em;
            border-radius: 0.4em;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    h1,
    p,
    span {
        color: white;
    }

    span.value {
        font-weight: var(--custom-font-weight-bold);
    }

    text-align: center;

    &.perspective {
        span.value {
            color: var(--ion-color-green);
        }

        h1 {
            .perspective-plus {
                background-color: var(--ion-color-green);
                color: var(--ion-color-white);
            }
        }
    }
}
</style>
